/**
 * RoleBadge — Compact chip displaying a role assignment on a Gantt task row.
 *
 * The entire badge text is colored by RACI type for immediate scannability:
 *   R = green, A = blue, C = yellow, I = grey.
 * In edit mode, clicking opens a popover with options to change RACI type
 * or remove the assignment.
 *
 * Epic: DEV-625 prerequisite (Role-Based Gantt Task Assignment)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { TaskRoleAssignmentEnriched, ResponsibilityType } from '../../api/ganttTaskRoleAssignments';
import { RESPONSIBILITY_TYPES } from '../../api/ganttTaskRoleAssignments';
import styles from './RoleBadge.module.css';

/* ------------------------------------------------------------------ */
/*  RACI style mapping                                                 */
/* ------------------------------------------------------------------ */

const RACI_STYLE: Record<string, string> = {
  responsible: styles.raciResponsible,
  accountable: styles.raciAccountable,
  consulted:   styles.raciConsulted,
  informed:    styles.raciInformed,
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface RoleBadgeProps {
  assignment: TaskRoleAssignmentEnriched;
  canEdit?: boolean;
  onRemove?: (assignmentId: string) => void;
  onChangeType?: (assignmentId: string, newType: ResponsibilityType) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function RoleBadge({
  assignment,
  canEdit = false,
  onRemove,
  onChangeType,
}: RoleBadgeProps) {
  const { t } = useAppTranslation('pages');
  const [showPopover, setShowPopover] = useState(false);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!showPopover) return;
    function handleClick(e: MouseEvent) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        badgeRef.current && !badgeRef.current.contains(e.target as Node)
      ) {
        setShowPopover(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPopover]);

  const handleBadgeClick = useCallback((e: React.MouseEvent) => {
    if (!canEdit) return;
    e.stopPropagation();
    setShowPopover((prev) => !prev);
  }, [canEdit]);

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPopover(false);
    onRemove?.(assignment.id);
  }, [assignment.id, onRemove]);

  const handleChangeType = useCallback((newType: ResponsibilityType) => {
    setShowPopover(false);
    onChangeType?.(assignment.id, newType);
  }, [assignment.id, onChangeType]);

  const raciClass = RACI_STYLE[assignment.responsibilityType] || RACI_STYLE.responsible;

  // i18n label for RACI type
  const raciLabel = t(`gantt.${assignment.responsibilityType}`) || assignment.responsibilityType;

  // Compact label: abbreviated role + RACI letter, e.g. "Prog Coord (R)"
  const raciLetter = (assignment.responsibilityType || 'responsible').charAt(0).toUpperCase();

  return (
    <>
      <span
        ref={badgeRef}
        data-role-badge
        className={`${styles.badge} ${raciClass} ${canEdit ? styles.badgeClickable : ''}`}
        onClick={handleBadgeClick}
        title={`${assignment.roleLabel} (${raciLabel})`}
      >
        <span className={styles.badgeLabel}>{assignment.roleLabel}</span>
        <span className={styles.badgeRaci}>({raciLetter})</span>
      </span>

      {showPopover && canEdit && (() => {
        // Position the popover at the badge location, rendered via portal
        // to escape overflow:hidden containers (roles cell, badge group).
        const rect = badgeRef.current?.getBoundingClientRect();
        // z-index must exceed editorOverlay (100000) to appear above Gantt pop-out
        const popoverStyle: React.CSSProperties = rect
          ? { position: 'fixed', top: rect.bottom + 4, left: rect.left, zIndex: 100001 }
          : { position: 'fixed', top: 0, left: 0, zIndex: 100001 };

        return createPortal(
          <div
            ref={popoverRef}
            className={styles.popover}
            style={popoverStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.popoverHeader}>
              <div className={styles.popoverTitle}>{assignment.roleLabel}</div>
              <div className={styles.popoverMeta}>
                {t(`categories.${assignment.roleCategory}`, { ns: 'community' }) || assignment.roleCategory}
                {' \u2022 '}
                {raciLabel}
              </div>
            </div>

            {/* RACI type switcher */}
            {RESPONSIBILITY_TYPES.map((rType) => (
              <button
                key={rType}
                className={styles.popoverAction}
                onClick={() => handleChangeType(rType)}
                disabled={rType === assignment.responsibilityType}
                style={rType === assignment.responsibilityType ? { opacity: 0.5 } : undefined}
              >
                <span className={`${styles.raciDot} ${RACI_STYLE[rType]}`} />
                {t(`gantt.${rType}`) || rType}
              </button>
            ))}

            {/* Remove action */}
            <button
              className={`${styles.popoverAction} ${styles.popoverDanger}`}
              onClick={handleRemove}
            >
              {t('gantt.removeRoleAssignment')}
            </button>
          </div>,
          document.body
        );
      })()}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  BadgeGroup — renders multiple badges for a task cell               */
/* ------------------------------------------------------------------ */

export interface RoleBadgeGroupProps {
  assignments: TaskRoleAssignmentEnriched[];
  canEdit?: boolean;
  maxVisible?: number;
  onRemove?: (assignmentId: string) => void;
  onChangeType?: (assignmentId: string, newType: ResponsibilityType) => void;
}

export function RoleBadgeGroup({
  assignments,
  canEdit = false,
  maxVisible = 3,
  onRemove,
  onChangeType,
}: RoleBadgeGroupProps) {
  if (!assignments || assignments.length === 0) return null;

  const visible = assignments.slice(0, maxVisible);
  const overflow = assignments.length - maxVisible;

  return (
    <div className={styles.badgeGroup}>
      {visible.map((a) => (
        <RoleBadge
          key={a.id}
          assignment={a}
          canEdit={canEdit}
          onRemove={onRemove}
          onChangeType={onChangeType}
        />
      ))}
      {overflow > 0 && (
        <span className={styles.overflowCount} title={`+${overflow} more roles`}>
          +{overflow}
        </span>
      )}
    </div>
  );
}
